/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU128Decoder,
  getU128Encoder,
  getU64Decoder,
  getU64Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const REDEEM_PROTOCOL_SHARES_DISCRIMINATOR = new Uint8Array([
  143, 253, 251, 77, 213, 178, 0, 225,
]);

export function getRedeemProtocolSharesDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    REDEEM_PROTOCOL_SHARES_DISCRIMINATOR,
  );
}

export type RedeemProtocolSharesInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountProtocolFeePosition extends string | AccountMeta<string> = string,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountVault0 extends string | AccountMeta<string> = string,
  TAccountVault1 extends string | AccountMeta<string> = string,
  TAccountToken0Mint extends string | AccountMeta<string> = string,
  TAccountToken1Mint extends string | AccountMeta<string> = string,
  TAccountRecipient0 extends string | AccountMeta<string> = string,
  TAccountRecipient1 extends string | AccountMeta<string> = string,
  TAccountToken0Program extends string | AccountMeta<string> = string,
  TAccountToken1Program extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? ReadonlyAccount<TAccountConfig>
        : TAccountConfig,
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountProtocolFeePosition extends string
        ? WritableAccount<TAccountProtocolFeePosition>
        : TAccountProtocolFeePosition,
      TAccountAdmin extends string
        ? ReadonlySignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      TAccountAuthority extends string
        ? ReadonlyAccount<TAccountAuthority>
        : TAccountAuthority,
      TAccountVault0 extends string
        ? WritableAccount<TAccountVault0>
        : TAccountVault0,
      TAccountVault1 extends string
        ? WritableAccount<TAccountVault1>
        : TAccountVault1,
      TAccountToken0Mint extends string
        ? ReadonlyAccount<TAccountToken0Mint>
        : TAccountToken0Mint,
      TAccountToken1Mint extends string
        ? ReadonlyAccount<TAccountToken1Mint>
        : TAccountToken1Mint,
      TAccountRecipient0 extends string
        ? WritableAccount<TAccountRecipient0>
        : TAccountRecipient0,
      TAccountRecipient1 extends string
        ? WritableAccount<TAccountRecipient1>
        : TAccountRecipient1,
      TAccountToken0Program extends string
        ? ReadonlyAccount<TAccountToken0Program>
        : TAccountToken0Program,
      TAccountToken1Program extends string
        ? ReadonlyAccount<TAccountToken1Program>
        : TAccountToken1Program,
      ...TRemainingAccounts,
    ]
  >;

export type RedeemProtocolSharesInstructionData = {
  discriminator: ReadonlyUint8Array;
  sharesIn: bigint;
  minAmount0Out: bigint;
  minAmount1Out: bigint;
};

export type RedeemProtocolSharesInstructionDataArgs = {
  sharesIn: number | bigint;
  minAmount0Out: number | bigint;
  minAmount1Out: number | bigint;
};

export function getRedeemProtocolSharesInstructionDataEncoder(): FixedSizeEncoder<RedeemProtocolSharesInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['sharesIn', getU128Encoder()],
      ['minAmount0Out', getU64Encoder()],
      ['minAmount1Out', getU64Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: REDEEM_PROTOCOL_SHARES_DISCRIMINATOR,
    }),
  );
}

export function getRedeemProtocolSharesInstructionDataDecoder(): FixedSizeDecoder<RedeemProtocolSharesInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['sharesIn', getU128Decoder()],
    ['minAmount0Out', getU64Decoder()],
    ['minAmount1Out', getU64Decoder()],
  ]);
}

export function getRedeemProtocolSharesInstructionDataCodec(): FixedSizeCodec<
  RedeemProtocolSharesInstructionDataArgs,
  RedeemProtocolSharesInstructionData
> {
  return combineCodec(
    getRedeemProtocolSharesInstructionDataEncoder(),
    getRedeemProtocolSharesInstructionDataDecoder(),
  );
}

export type RedeemProtocolSharesAsyncInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
  TAccountProtocolFeePosition extends string = string,
  TAccountAdmin extends string = string,
  TAccountAuthority extends string = string,
  TAccountVault0 extends string = string,
  TAccountVault1 extends string = string,
  TAccountToken0Mint extends string = string,
  TAccountToken1Mint extends string = string,
  TAccountRecipient0 extends string = string,
  TAccountRecipient1 extends string = string,
  TAccountToken0Program extends string = string,
  TAccountToken1Program extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  protocolFeePosition: Address<TAccountProtocolFeePosition>;
  admin: TransactionSigner<TAccountAdmin>;
  authority?: Address<TAccountAuthority>;
  vault0: Address<TAccountVault0>;
  vault1: Address<TAccountVault1>;
  token0Mint: Address<TAccountToken0Mint>;
  token1Mint: Address<TAccountToken1Mint>;
  recipient0: Address<TAccountRecipient0>;
  recipient1: Address<TAccountRecipient1>;
  token0Program: Address<TAccountToken0Program>;
  token1Program: Address<TAccountToken1Program>;
  sharesIn: RedeemProtocolSharesInstructionDataArgs['sharesIn'];
  minAmount0Out: RedeemProtocolSharesInstructionDataArgs['minAmount0Out'];
  minAmount1Out: RedeemProtocolSharesInstructionDataArgs['minAmount1Out'];
};

export async function getRedeemProtocolSharesInstructionAsync<
  TAccountConfig extends string,
  TAccountPool extends string,
  TAccountProtocolFeePosition extends string,
  TAccountAdmin extends string,
  TAccountAuthority extends string,
  TAccountVault0 extends string,
  TAccountVault1 extends string,
  TAccountToken0Mint extends string,
  TAccountToken1Mint extends string,
  TAccountRecipient0 extends string,
  TAccountRecipient1 extends string,
  TAccountToken0Program extends string,
  TAccountToken1Program extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: RedeemProtocolSharesAsyncInput<
    TAccountConfig,
    TAccountPool,
    TAccountProtocolFeePosition,
    TAccountAdmin,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountRecipient0,
    TAccountRecipient1,
    TAccountToken0Program,
    TAccountToken1Program
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  RedeemProtocolSharesInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountProtocolFeePosition,
    TAccountAdmin,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountRecipient0,
    TAccountRecipient1,
    TAccountToken0Program,
    TAccountToken1Program
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: true },
    protocolFeePosition: {
      value: input.protocolFeePosition ?? null,
      isWritable: true,
    },
    admin: { value: input.admin ?? null, isWritable: false },
    authority: { value: input.authority ?? null, isWritable: false },
    vault0: { value: input.vault0 ?? null, isWritable: true },
    vault1: { value: input.vault1 ?? null, isWritable: true },
    token0Mint: { value: input.token0Mint ?? null, isWritable: false },
    token1Mint: { value: input.token1Mint ?? null, isWritable: false },
    recipient0: { value: input.recipient0 ?? null, isWritable: true },
    recipient1: { value: input.recipient1 ?? null, isWritable: true },
    token0Program: { value: input.token0Program ?? null, isWritable: false },
    token1Program: { value: input.token1Program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.authority.value) {
    accounts.authority.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([97, 117, 116, 104, 111, 114, 105, 116, 121]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount('pool', accounts.pool.value),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('protocolFeePosition', accounts.protocolFeePosition),
      getAccountMeta('admin', accounts.admin),
      getAccountMeta('authority', accounts.authority),
      getAccountMeta('vault0', accounts.vault0),
      getAccountMeta('vault1', accounts.vault1),
      getAccountMeta('token0Mint', accounts.token0Mint),
      getAccountMeta('token1Mint', accounts.token1Mint),
      getAccountMeta('recipient0', accounts.recipient0),
      getAccountMeta('recipient1', accounts.recipient1),
      getAccountMeta('token0Program', accounts.token0Program),
      getAccountMeta('token1Program', accounts.token1Program),
    ],
    data: getRedeemProtocolSharesInstructionDataEncoder().encode(
      args as RedeemProtocolSharesInstructionDataArgs,
    ),
    programAddress,
  } as RedeemProtocolSharesInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountProtocolFeePosition,
    TAccountAdmin,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountRecipient0,
    TAccountRecipient1,
    TAccountToken0Program,
    TAccountToken1Program
  >);
}

export type RedeemProtocolSharesInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
  TAccountProtocolFeePosition extends string = string,
  TAccountAdmin extends string = string,
  TAccountAuthority extends string = string,
  TAccountVault0 extends string = string,
  TAccountVault1 extends string = string,
  TAccountToken0Mint extends string = string,
  TAccountToken1Mint extends string = string,
  TAccountRecipient0 extends string = string,
  TAccountRecipient1 extends string = string,
  TAccountToken0Program extends string = string,
  TAccountToken1Program extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  protocolFeePosition: Address<TAccountProtocolFeePosition>;
  admin: TransactionSigner<TAccountAdmin>;
  authority: Address<TAccountAuthority>;
  vault0: Address<TAccountVault0>;
  vault1: Address<TAccountVault1>;
  token0Mint: Address<TAccountToken0Mint>;
  token1Mint: Address<TAccountToken1Mint>;
  recipient0: Address<TAccountRecipient0>;
  recipient1: Address<TAccountRecipient1>;
  token0Program: Address<TAccountToken0Program>;
  token1Program: Address<TAccountToken1Program>;
  sharesIn: RedeemProtocolSharesInstructionDataArgs['sharesIn'];
  minAmount0Out: RedeemProtocolSharesInstructionDataArgs['minAmount0Out'];
  minAmount1Out: RedeemProtocolSharesInstructionDataArgs['minAmount1Out'];
};

export function getRedeemProtocolSharesInstruction<
  TAccountConfig extends string,
  TAccountPool extends string,
  TAccountProtocolFeePosition extends string,
  TAccountAdmin extends string,
  TAccountAuthority extends string,
  TAccountVault0 extends string,
  TAccountVault1 extends string,
  TAccountToken0Mint extends string,
  TAccountToken1Mint extends string,
  TAccountRecipient0 extends string,
  TAccountRecipient1 extends string,
  TAccountToken0Program extends string,
  TAccountToken1Program extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: RedeemProtocolSharesInput<
    TAccountConfig,
    TAccountPool,
    TAccountProtocolFeePosition,
    TAccountAdmin,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountRecipient0,
    TAccountRecipient1,
    TAccountToken0Program,
    TAccountToken1Program
  >,
  config?: { programAddress?: TProgramAddress },
): RedeemProtocolSharesInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountPool,
  TAccountProtocolFeePosition,
  TAccountAdmin,
  TAccountAuthority,
  TAccountVault0,
  TAccountVault1,
  TAccountToken0Mint,
  TAccountToken1Mint,
  TAccountRecipient0,
  TAccountRecipient1,
  TAccountToken0Program,
  TAccountToken1Program
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: true },
    protocolFeePosition: {
      value: input.protocolFeePosition ?? null,
      isWritable: true,
    },
    admin: { value: input.admin ?? null, isWritable: false },
    authority: { value: input.authority ?? null, isWritable: false },
    vault0: { value: input.vault0 ?? null, isWritable: true },
    vault1: { value: input.vault1 ?? null, isWritable: true },
    token0Mint: { value: input.token0Mint ?? null, isWritable: false },
    token1Mint: { value: input.token1Mint ?? null, isWritable: false },
    recipient0: { value: input.recipient0 ?? null, isWritable: true },
    recipient1: { value: input.recipient1 ?? null, isWritable: true },
    token0Program: { value: input.token0Program ?? null, isWritable: false },
    token1Program: { value: input.token1Program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('protocolFeePosition', accounts.protocolFeePosition),
      getAccountMeta('admin', accounts.admin),
      getAccountMeta('authority', accounts.authority),
      getAccountMeta('vault0', accounts.vault0),
      getAccountMeta('vault1', accounts.vault1),
      getAccountMeta('token0Mint', accounts.token0Mint),
      getAccountMeta('token1Mint', accounts.token1Mint),
      getAccountMeta('recipient0', accounts.recipient0),
      getAccountMeta('recipient1', accounts.recipient1),
      getAccountMeta('token0Program', accounts.token0Program),
      getAccountMeta('token1Program', accounts.token1Program),
    ],
    data: getRedeemProtocolSharesInstructionDataEncoder().encode(
      args as RedeemProtocolSharesInstructionDataArgs,
    ),
    programAddress,
  } as RedeemProtocolSharesInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountProtocolFeePosition,
    TAccountAdmin,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountRecipient0,
    TAccountRecipient1,
    TAccountToken0Program,
    TAccountToken1Program
  >);
}

export type ParsedRedeemProtocolSharesInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    pool: TAccountMetas[1];
    protocolFeePosition: TAccountMetas[2];
    admin: TAccountMetas[3];
    authority: TAccountMetas[4];
    vault0: TAccountMetas[5];
    vault1: TAccountMetas[6];
    token0Mint: TAccountMetas[7];
    token1Mint: TAccountMetas[8];
    recipient0: TAccountMetas[9];
    recipient1: TAccountMetas[10];
    token0Program: TAccountMetas[11];
    token1Program: TAccountMetas[12];
  };
  data: RedeemProtocolSharesInstructionData;
};

export function parseRedeemProtocolSharesInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedRedeemProtocolSharesInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 13) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 13,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      config: getNextAccount(),
      pool: getNextAccount(),
      protocolFeePosition: getNextAccount(),
      admin: getNextAccount(),
      authority: getNextAccount(),
      vault0: getNextAccount(),
      vault1: getNextAccount(),
      token0Mint: getNextAccount(),
      token1Mint: getNextAccount(),
      recipient0: getNextAccount(),
      recipient1: getNextAccount(),
      token0Program: getNextAccount(),
      token1Program: getNextAccount(),
    },
    data: getRedeemProtocolSharesInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
